import { differenceInCalendarDays } from "date-fns"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import type { CustomerInsightQuery } from "@/v2-backoffice-client/customer/insight/__types__/customer.insight.query"
import {
  deriveScopeUsageSignals,
  type UsageInsight,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"

type CompanyProfile = NonNullable<
  CustomerInsightQuery["getCompanyProfileByOrganizationNumber"]
>

const RENEWAL_URGENCY_DAYS = 120
const EXPANSION_HEADROOM_FACTOR = 3
const ACTIVE_SHARE_HEALTHY = 0.6

/**
 * Registry-fact indicator: shares the usage plays' tones (and therefore
 * icons/colors) but is NOT a usage play — no play kind, and it never enters
 * the Portfolio playbook.
 */
export type CompanyIndicator = Pick<UsageInsight, "tone" | "text">

/**
 * Where the company registry facts intersect with the subscription and the
 * observed usage — the "so what" lines for the sales conversation. Company
 * size vs licenses says expansion or right-sizing; the renewal date plus the
 * churn read says how urgent; the property footprint says which modules to
 * pitch. Same tones as the usage plays so the two lists read as one system.
 *
 * The renewal indicator reads LicenseRenewsAt — the next renewal among the
 * customer's licenses that will bill again (lic.Licenses), a billing fact
 * the license domain owns (D7).
 */
export function deriveCompanyIndicators(
  profile: CompanyProfile,
  customer: CustomerFragment,
): CompanyIndicator[] {
  const indicators: CompanyIndicator[] = []
  const signals = deriveScopeUsageSignals(customer)

  if (profile.IsActive === false || profile.BankruptAt) {
    indicators.push({
      tone: "action",
      text: profile.BankruptAt
        ? "The registry lists this company as bankrupt — verify the contracting entity before the next renewal."
        : "The registry lists this company as inactive — verify the contracting entity before the next renewal.",
    })
  }

  if (customer.LicenseRenewsAt) {
    const days = differenceInCalendarDays(
      new Date(customer.LicenseRenewsAt),
      new Date(),
    )
    if (days >= 0 && days <= RENEWAL_URGENCY_DAYS) {
      const risk = signals?.churnRisk.level
      indicators.push({
        tone: risk === "high" || risk === "medium" ? "action" : "positive",
        text:
          `Contract renews in ${days} day${days === 1 ? "" : "s"}.` +
          (risk === "high" || risk === "medium"
            ? " Churn risk is elevated — run the activation plays before the renewal call."
            : " Usage signals look healthy going into the renewal."),
      })
    }
  }

  // Licensed seats from lic.Licenses (the licenseFacts sum of live seat
  // caps) — uncapped licenses (null) have no headroom story to tell.
  const employees = profile.NumberOfEmployees
  const licensedSeats = customer.LicensedSeats
  if (employees != null && licensedSeats != null && licensedSeats > 0) {
    const activeShare =
      signals && signals.seatCount > 0
        ? signals.activeCount / signals.seatCount
        : null
    if (
      employees >= licensedSeats * EXPANSION_HEADROOM_FACTOR &&
      (activeShare == null || activeShare >= ACTIVE_SHARE_HEALTHY)
    ) {
      indicators.push({
        tone: "opportunity",
        text: `~${employees} employees but only ${licensedSeats} licensed seat${licensedSeats === 1 ? "" : "s"} — clear expansion headroom if the current seats stay engaged.`,
      })
    }
  }

  if (
    profile.OwnedPropertiesCount != null &&
    profile.OwnedPropertiesCount > 0
  ) {
    indicators.push({
      tone: "opportunity",
      text: `The company owns ${profile.OwnedPropertiesCount} propert${profile.OwnedPropertiesCount === 1 ? "y" : "ies"} in the registry — portfolio monitoring, transactions and value tracking map directly onto that footprint.`,
    })
  }

  if (profile.SubsidiaryCount != null && profile.SubsidiaryCount > 0) {
    indicators.push({
      tone: "opportunity",
      text: `${profile.SubsidiaryCount} active subsidiar${profile.SubsidiaryCount === 1 ? "y" : "ies"} — a group-level licensing conversation may fit better than per-company seats.`,
    })
  }

  return indicators
}
